// src/pages/SharedDeliveryView.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, CircularProgress,
  Alert, Divider, Chip, Button, Stack,
} from '@mui/material';
import { MapPin, ArrowRight, Clock, Calendar, Truck, AlertCircle, CheckCircle2 } from 'lucide-react';
import { getSharedDelivery } from '../utils/deliveryShare';
import { useAuth } from '../context/AuthContext';
import { useDelivery } from '../context/DeliveryContext';

function toDateMaybe(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') { const d = new Date(value); return isNaN(d.getTime()) ? null : d; }
  if (value instanceof Date) return value;
  return null;
}

function formatDateTime(date) {
  if (!date) return '—';
  return date.toLocaleString('fi-FI', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

const transportLabel = (type) => ({
  'express':     'Pikakuljetus',
  'eco-bus':     'Bussipaketti',
  'eco-regular': 'Eko-kuljetus',
  'eco-bid':     'Tarjouskuljetus',
}[type] || type);

export function SharedDeliveryView() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { acceptDelivery } = useDelivery();

  const [delivery, setDelivery] = useState(null);
  const [deliveryId, setDeliveryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shareId) return;
    (async () => {
      try {
        const data = await getSharedDelivery(shareId);
        if (!data) { setError('Kuljetusta ei löytynyt tai linkki on vanhentunut.'); return; }
        setDeliveryId(data.deliveryId || null);
        setDelivery(data);
      } catch {
        setError('Virhe ladattaessa kuljetustietoja.');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  const handleAccept = async () => {
    if (!user) {
      const returnTo = `/kuljeta/${shareId}`;
      if (delivery?.organizationId) {
        navigate(`/create-user?organization=${delivery.organizationId}`, { state: { from: { pathname: returnTo } } });
      } else {
        navigate('/create-user', { state: { from: { pathname: returnTo } } });
      }
      return;
    }
    if (!deliveryId) { setError('Kuljetuksen tunniste puuttuu. Linkki voi olla virheellinen.'); return; }
    try {
      setAccepting(true);
      await acceptDelivery(deliveryId);
      setAccepted(true);
      setTimeout(() => navigate('/kuljeta-paketti?tab=own'), 1800);
    } catch {
      setError('Virhe hyväksyttäessä kuljetusta. Yritä uudelleen.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ pt: 16, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Ladataan...</Typography>
      </Container>
    );
  }

  if (error && !delivery) {
    return (
      <Container maxWidth="sm" sx={{ pt: 16 }}>
        <Alert severity="error" icon={<AlertCircle size={20} />} sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>Palaa etusivulle</Button>
      </Container>
    );
  }

  if (accepted) {
    return (
      <Container maxWidth="sm" sx={{ pt: 16, textAlign: 'center' }}>
        <Alert severity="success" icon={<CheckCircle2 size={20} />}>
          Kuljetus hyväksytty! Sinut ohjataan kuljetussivulle...
        </Alert>
      </Container>
    );
  }

  const origin      = delivery.origin      ?? delivery.from ?? '—';
  const destination = delivery.destination ?? delivery.to   ?? '—';
  const pickupDate  = toDateMaybe(delivery.pickupTime);
  const targetDate  = toDateMaybe(delivery.targetTime);

  const city = delivery.originInfo?.city || origin || null;
  const distanceKm = delivery.distance ? Math.round(delivery.distance)/1000 : null;
  const deliveryTitle = [
    'Kuljetustoimeksianto',
    city,
    distanceKm ? `${distanceKm} km` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Container maxWidth="sm" sx={{ pt: 14, pb: 6 }}>
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>

        {/* Title */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary" noWrap>
            {deliveryTitle}
          </Typography>
        </Box>

        {/* Reward banner */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', px: 3, py: 2.5, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ opacity: 0.75, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Kuljettajan palkkio
          </Typography>
          <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.1 }}>
            {delivery.reward ? `${delivery.reward} €` : '—'}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {delivery.urgent && (
            <Alert severity="warning" sx={{ mb: 2 }}>⚡ Kiireellinen kuljetus</Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {/* Route */}
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <MapPin size={16} style={{ marginTop: 2, flexShrink: 0, color: '#64748b' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Nouto</Typography>
                <Typography variant="body2" fontWeight={500}>{origin}</Typography>
              </Box>
            </Box>
            <Box sx={{ pl: '6px' }}>
              <ArrowRight size={16} color="#cbd5e1" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <MapPin size={16} style={{ marginTop: 2, flexShrink: 0, color: '#2563eb' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Toimitus</Typography>
                <Typography variant="body2" fontWeight={500}>{destination}</Typography>
              </Box>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Times */}
          {(pickupDate || targetDate) && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                {pickupDate && (
                  <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Calendar size={13} />
                      <Typography variant="caption" color="text.secondary">Noutoaika</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>{formatDateTime(pickupDate)}</Typography>
                  </Box>
                )}
                {targetDate && (
                  <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Clock size={13} />
                      <Typography variant="caption" color="text.secondary">Toimitusaika</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>{formatDateTime(targetDate)}</Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Chips */}
          {(delivery.deliveryTransportType || delivery.returnParcels || delivery.temperatureControl) && (
            <>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
                {delivery.deliveryTransportType && (
                  <Chip label={transportLabel(delivery.deliveryTransportType)} size="small" variant="outlined" />
                )}
                {delivery.returnParcels && (
                  <Chip
                    label={`Palautuskuljetus${delivery.returnMethod ? ` · ${delivery.returnMethod === 'posti' ? 'Posti' : 'Bus'}` : ''}`}
                    size="small" color="warning" variant="outlined"
                  />
                )}
                {delivery.temperatureControl && (
                  <Chip label="Lämpötilahallinta" size="small" color="info" variant="outlined" />
                )}
              </Stack>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Accept */}
          <Button
            fullWidth variant="contained" size="large"
            onClick={handleAccept} disabled={accepting}
            startIcon={accepting ? <CircularProgress size={18} color="inherit" /> : <Truck size={18} />}
            sx={{ borderRadius: 2 }}
          >
            {accepting ? 'Hyväksytään...' : 'Hyväksy kuljetus'}
          </Button>

          {!user && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              Hyväksyminen vaatii kirjautumisen.
            </Typography>
          )}
        </Box>
      </Paper>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
        Jaettu kuljetuslinkki · SBP Oy
      </Typography>
    </Container>
  );
}